import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
} from '@design-system';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { endOfDay, format, startOfDay } from 'date-fns';
import { cn } from '@client/lib';
import { DateFilterState, DatePreset, MetricsOrganization } from '../types/metrics-type';

interface MetricsOrganizationFilterProps {
  organization: MetricsOrganization;
  onOrganizationChange: (organization: MetricsOrganization) => void;
  idPrefix: string;
  compact?: boolean;
}

interface MetricsDateFilterProps {
  dateFilter: DateFilterState;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDatesChange: (startDate: Date, endDate: Date) => void;
  organization?: MetricsOrganization;
  onOrganizationChange?: (organization: MetricsOrganization) => void;
  idPrefix?: string;
  compact?: boolean;
  className?: string;
}

interface MetricsAppliedFiltersProps {
  filterKey?: string | null;
  onRemoveFilter?: () => void;
}

const METRIC_FILTER_LABELS: Record<string, string> = {
  newInPeriod: 'Advisors Added',
  wizard: 'Wizard Advisors',
  activeAccess: 'Active Advisors',
  withoutActiveAccess: 'Advisors Without Active Access',
  activeInPeriod: 'Advisors With Activity',
  inactiveInPeriod: 'Advisors Without Activity',
  active: 'Active Subscriptions',
  trialing: 'Trial Subscriptions',
  canceled: 'Canceled Subscriptions',
  otherInactive: 'Other Inactive Subscriptions',
  inactive: 'Inactive Subscriptions',
  cancellationsWeekly: 'Weekly Cancellations',
  cancellationsMonthly: 'Monthly Cancellations',
  cancellationsInPeriod: 'Cancellations in Period',
};

type SelectablePreset = Exclude<DatePreset, 'allTime' | 'custom'>;

const PRESET_OPTIONS: { value: SelectablePreset; label: string }[] = [
  { value: 'last7days', label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
  { value: 'last90days', label: 'Last 90 days' },
  { value: 'thisYear', label: 'This year' },
];

function presetLabel(preset: DatePreset): string {
  if (preset === 'allTime') return 'All time';
  if (preset === 'custom') return 'Custom range';
  return PRESET_OPTIONS.find(option => option.value === preset)?.label ?? 'Custom range';
}

function triggerLabel(dateFilter: DateFilterState): string {
  if (dateFilter.preset === 'allTime') return 'All time';
  return `${presetLabel(dateFilter.preset)} · ${format(dateFilter.startDate, 'MMM d')} – ${format(
    dateFilter.endDate,
    'MMM d',
  )}`;
}

export function MetricsOrganizationFilter({
  organization,
  onOrganizationChange,
  idPrefix = 'metrics',
  compact = false,
}: MetricsOrganizationFilterProps) {
  const selectId = `${idPrefix}-organization`;

  return (
    <div className={compact ? 'w-full sm:w-64 sm:flex-none' : 'w-full sm:w-72 sm:flex-none'}>
      <Label
        htmlFor={selectId}
        className={compact ? 'sr-only' : 'mb-2 block text-sm font-medium text-gray-700'}
      >
        Partner
      </Label>
      <Select
        value={organization}
        onValueChange={value => onOrganizationChange(value as MetricsOrganization)}
      >
        <SelectTrigger
          id={selectId}
          className="w-full"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All partners</SelectItem>
          <SelectItem value="nyl">New York Life</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export function MetricsAppliedFilters({ filterKey, onRemoveFilter }: MetricsAppliedFiltersProps) {
  const metricLabel =
    filterKey && filterKey !== 'all' ? METRIC_FILTER_LABELS[filterKey] : undefined;
  if (!metricLabel) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      aria-label="Applied filters"
    >
      <span className="text-sm font-medium text-gray-700">Applied filters</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRemoveFilter}
        className="bg-primary/10 text-primary hover:bg-primary/15 border-primary/20 h-auto gap-1.5 px-2.5 py-1 text-sm font-medium"
      >
        Metric: {metricLabel}
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

export function MetricsDateFilter({
  dateFilter,
  onPresetChange,
  onCustomDatesChange,
  organization,
  onOrganizationChange,
  idPrefix = 'metrics',
  compact = false,
  className = '',
}: MetricsDateFilterProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateFilterState | undefined>(() =>
    dateFilter.preset === 'allTime' ? undefined : dateFilter,
  );
  const today = new Date();
  const hasActiveFilter = dateFilter.preset !== 'allTime';
  const defaultMonth = new Date(draft?.startDate ?? today);
  defaultMonth.setMonth(defaultMonth.getMonth() - 1);

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      setDraft(dateFilter.preset === 'allTime' ? undefined : dateFilter);
    }
    setOpen(nextOpen);
  }

  function handlePresetClick(preset: SelectablePreset) {
    onPresetChange(preset);
    setOpen(false);
  }

  function handleCustomClick() {
    setDraft(current =>
      current
        ? { ...current, preset: 'custom' }
        : {
            preset: 'custom',
            startDate: startOfDay(today),
            endDate: endOfDay(today),
          },
    );
  }

  function handleCalendarSelect(selected: { from?: Date; to?: Date } | undefined) {
    if (!selected?.from) return;
    setDraft({
      preset: 'custom',
      startDate: startOfDay(selected.from),
      endDate: endOfDay(selected.to ?? selected.from),
    });
  }

  function handleClear() {
    setDraft(undefined);
    onPresetChange('allTime');
    setOpen(false);
  }

  function handleApply() {
    if (!draft) return;
    onCustomDatesChange(draft.startDate, draft.endDate);
    setOpen(false);
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center',
        compact && 'sm:flex-nowrap',
        className,
      )}
    >
      {organization !== undefined && onOrganizationChange && (
        <MetricsOrganizationFilter
          organization={organization}
          onOrganizationChange={onOrganizationChange}
          idPrefix={idPrefix}
          compact={compact}
        />
      )}

      <Popover
        open={open}
        onOpenChange={handleOpenChange}
      >
        <div className="relative w-full sm:w-auto">
          <PopoverTrigger asChild>
            <Button
              id={`${idPrefix}-date-range`}
              type="button"
              variant="outline"
              className={cn(
                'w-full justify-start sm:w-auto',
                hasActiveFilter && 'bg-primary/10 border-primary',
              )}
            >
              <CalendarIcon className="h-4 w-4 shrink-0" />
              <span className={cn(hasActiveFilter && 'pr-7')}>{triggerLabel(dateFilter)}</span>
            </Button>
          </PopoverTrigger>
          {hasActiveFilter && (
            <button
              type="button"
              aria-label="Clear date filter"
              className="text-primary absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 hover:opacity-70"
              onClick={event => {
                event.stopPropagation();
                handleClear();
              }}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <PopoverContent
          align="end"
          className="w-auto border p-0 shadow-xl"
        >
          <div className="flex">
            <div className="flex flex-col gap-1 border-r p-3 pr-2">
              {PRESET_OPTIONS.map(option => (
                <Button
                  key={option.value}
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePresetClick(option.value)}
                  className={cn(
                    'justify-start',
                    draft?.preset === option.value
                      ? 'bg-primary/15 text-primary-dark font-medium'
                      : 'text-muted-foreground',
                  )}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCustomClick}
                className={cn(
                  'justify-start',
                  draft?.preset === 'custom'
                    ? 'bg-primary/15 text-primary-dark font-medium'
                    : 'text-muted-foreground',
                )}
              >
                Custom range
              </Button>
            </div>

            <div className="flex flex-col">
              <div className="flex items-center gap-4 border-b px-4 py-3">
                <DateDisplay
                  label="Start"
                  date={draft?.startDate}
                />
                <DateDisplay
                  label="End"
                  date={draft?.endDate}
                />
              </div>
              <Calendar
                mode="range"
                selected={draft ? { from: draft.startDate, to: draft.endDate } : undefined}
                onSelect={handleCalendarSelect}
                numberOfMonths={2}
                defaultMonth={defaultMonth}
                disabled={{ after: today }}
              />
              <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleClear}
                >
                  Clear
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleApply}
                  disabled={!draft}
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function DateDisplay({ label, date }: { label: string; date?: Date }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground text-sm">{label}</span>
      <div
        className={cn(
          'border-input bg-background inline-flex h-9 items-center rounded-md border px-3 text-sm',
          !date && 'text-muted-foreground',
        )}
      >
        <CalendarIcon className="mr-1 h-3.5 w-3.5 shrink-0" />
        {date ? format(date, 'MMM d, yyyy') : 'mm/dd/yyyy'}
      </div>
    </div>
  );
}
